import { useTheme } from '@emotion/react'
import BreadcrumbHeader from 'layout/BreadcrumbsHeader'
import React, { useCallback, useState } from 'react'
import NeedHelpButton from 'ui-components/NeedHelpButton'
import ThemeBGWrapper from 'ui-components/ThemeBGWrapper'
import CustomTabs from 'ui-components/CustomTabs'
import { TabContext } from '@mui/lab'
import AsyncTableListing from 'ui-components/AsyncTableListing'
import { Box, Grid, Button, MenuItem, Select, InputAdornment, IconButton, useMediaQuery, Autocomplete, Tooltip, Typography } from '@mui/material'
import CustomInputField from 'ui-components/CustomInputField'
import { ReactComponent as SubscriptionLogsInfoIcon } from 'assets/info_icon.svg'
import { Search as SearchIcon, Visibility as VisibilityIcon, Receipt as ReceiptIcon, Search, FilterAltRounded, FilterAltOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { makeStyles } from '@mui/styles'
import { Primary, Secondary } from 'ui-components/Buttons';import ModalComponent from 'ui-components/ModalComponent'
import FilterPopup from './popups/FilterPopup'
import Cookies from 'js-cookie'
import { FetchAllSubscriptions, FetchInvoices } from 'api/startup_purchases'
import moment from 'moment'
import { capitalizedSentence, planVersionInfo } from 'utils/index'
import { FetchAllStartupPlans } from 'api/utils'
import placeHolderImage from "assets/no_data_images/BackrrStartups/no_data_feedbacks_views.svg"

const useStyles = makeStyles((theme) => ({
  scrollTabs: {
    "&": {
      padding: "3rem"
    }
    // '& .MuiTabs-root > .MuiTabs-scroller > .MuiTabs-flexContainer' : {marginLeft: '2rem', gap: '5rem'}
  }
  //     '& .MuiTabs-root > .Mui-disabled.MuiTabs-scrollButtons': {
  //         display: 'none !important',
  //         transition: 'all 1s ease'
  //     },
  // }
}));

// Subscription activity log popover. Merges the two activity sources the API exposes:
//  - events[]              -> admin actions + subscription lifecycle events
//  - subscription_charged[] -> billing/charge records (present on older subscriptions
//                              that predate events[]), so every startup shows its activity.
const SUBSCRIPTION_EVENT_LABELS = {
  "admin.created": "Subscription Provided by Admin",
  "subscription.updated": "Updated",
  "subscription.upgrade_initiated": "Upgrade Initiated",
  "subscription.downgrade_scheduled": "Downgrade Scheduled",
  "subscription.scheduled_change_cancelled": "Scheduled Change Cancelled",
  "subscription.cancelled": "Cancelled",
  "subscription.deactivated": "Deactivated",
};

const subscriptionEventLabel = (type) =>
  SUBSCRIPTION_EVENT_LABELS[type] ||
  capitalizedSentence(String(type || "").split(".").pop().replace(/_/g, " ")) ||
  "Event";

// Who performed each action. The API stores no explicit actor, so it is inferred from the
// event type: lifecycle actions the customer triggers -> User; billing/webhook-driven
// events -> Razorpay (payment gateway); admin actions -> Admin.
const SUBSCRIPTION_EVENT_ACTORS = {
  "admin.created": "Admin",
  "subscription.cancelled": "User",
  "subscription.upgrade_initiated": "User",
  "subscription.downgrade_scheduled": "User",
  "subscription.scheduled_change_cancelled": "User",
  "subscription.updated": "Razorpay",
  "subscription.deactivated": "Razorpay",
};

const subscriptionEventActor = (type) =>
  SUBSCRIPTION_EVENT_ACTORS[type] ||
  (String(type || "").startsWith("admin") ? "Admin" : "Razorpay");

// timestamp may arrive in seconds (10-digit) or milliseconds (13-digit)
const formatSubscriptionLogTime = (ts) => {
  if (!ts && ts !== 0) return "";
  const m = Number(ts) > 1e12 ? moment(Number(ts)) : moment.unix(Number(ts));
  return m.isValid() ? m.format("DD-MMM-YYYY HH:mm:ss") : "";
};

// Build a unified, newest-first activity log for one subscription row.
export const buildSubscriptionLogs = (row) => {
  const logs = [];

  // normalize any unix timestamp (seconds or ms) to milliseconds, so all sources sort together
  const toMs = (t) => {
    const n = Number(t) || 0;
    return n > 1e12 ? n : n * 1000;
  };

  // human-readable plan name (e.g. "Pro · Yearly") from the row, instead of the raw plan id
  const planTitle = row?.plan_details?.title
    || (row?.plan_details?.name ? capitalizedSentence(String(row.plan_details.name)) : "");
  const planTenure = row?.plan_details?.billing_period
    ? capitalizedSentence(String(row.plan_details.billing_period))
    : "";
  const planName = planTitle ? (planTenure ? `${planTitle} · ${planTenure}` : planTitle) : "";

  // "<Plan> (Status)" using readable names; falls back to the raw plan id only if no title exists
  const detailFor = (planId, status) => {
    const name = planName || planId || "";
    if (!name) return status ? capitalizedSentence(String(status)) : "";
    return status ? `${name} (${capitalizedSentence(String(status))})` : name;
  };

  (Array.isArray(row?.events) ? row.events : []).forEach((event, i) => {
    logs.push({
      key: event?._id || `event-${i}`,
      label: subscriptionEventLabel(event?.event_type),
      // label already reads "...by Admin" for admin.created, so don't append the actor again
      actor: event?.event_type === "admin.created" ? "" : subscriptionEventActor(event?.event_type),
      ts: toMs(event?.timestamp),
      detail: event?.data?.plan_id ? detailFor(event.data.plan_id, event?.data?.status) : "",
      // The customer's own words for a cancellation (stored on the event by the cancel API).
      reason: String(event?.reason || event?.data?.reason || "").trim(),
    });
  });

  // Admin-provided subscriptions ("admin-" prefixed ids) are never billed through Razorpay,
  // so any charge records on them would read as a real payment. Skip them.
  const isAdminProvided = String(row?.subscription_id || "").includes("admin-");

  (isAdminProvided || !Array.isArray(row?.subscription_charged) ? [] : row.subscription_charged).forEach((charge, i) => {
    logs.push({
      key: charge?.payment_id || charge?.invoice_id || `charge-${i}`,
      label: "Subscription Charged",
      actor: "Razorpay",
      ts: toMs(charge?.created_at || charge?.paid_at || charge?.charge_at),
      detail: detailFor(charge?.plan_id, charge?.status),
    });
  });

  return logs.sort((a, b) => (b?.ts || 0) - (a?.ts || 0));
};

const SubscriptionLogsInfo = ({ row }) => {
  const logs = buildSubscriptionLogs(row);
  const content = logs.length === 0 ? (
    <Typography sx={{ fontSize: "0.8rem", textTransform: "none", whiteSpace: "nowrap" }}>
      No activity recorded.
    </Typography>
  ) : (
    <Box sx={{ maxHeight: 260, overflowY: "auto", whiteSpace: "normal", minWidth: 190, textAlign: "left", pr: 0.5 }}>
      {logs.map((log, i) => (
        <Box key={log.key} sx={{ py: 0.5, borderBottom: i < logs.length - 1 ? "1px solid #eee" : "none" }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", textTransform: "none" }}>
            {log.label}
            {log.actor && (
              <Typography component="span" sx={{ fontWeight: 400, fontSize: "0.72rem", color: "text.secondary", textTransform: "none", ml: 0.5 }}>
                · By {log.actor}
              </Typography>
            )}
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", textTransform: "none" }}>
            {formatSubscriptionLogTime(log.ts)}
          </Typography>
          {log.detail && (
            <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", textTransform: "none" }}>
              {log.detail}
            </Typography>
          )}
          {log.reason && (
            <Typography sx={{ fontSize: "0.7rem", color: "text.primary", textTransform: "none", mt: 0.25, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              Reason: {log.reason}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
  // Tooltip renders via a portal (escapes the table's overflow so the card is never clipped),
  // opens to the left over the table interior, and is interactive so long lists can be scrolled.
  return (
    <Tooltip
      title={content}
      placement="left"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "#fff",
            color: "text.primary",
            border: "1px solid #e0e0e0",
            borderRadius: "6px",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
            maxWidth: 340,
            p: 1.25,
          },
        },
        arrow: { sx: { color: "#fff", "&::before": { border: "1px solid #e0e0e0" } } },
      }}
    >
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}>
        <SubscriptionLogsInfoIcon style={{ width: 16, height: 16 }} />
      </Box>
    </Tooltip>
  );
};

/**
 * The plan on a subscription, with its version. A title alone cannot tell the
 * versions apart — Plus at 1,299 monthly and Plus at 15,000 yearly are different
 * plans with the same name — and which one a startup is on is the whole question
 * this column answers while both generations have live subscribers.
 */
const PlanTypeCell = ({ row, startupPlans }) => {
    const title = row?.plan_details?.title
        || (row?.plan_details?.name ? capitalizedSentence(String(row?.plan_details?.name)) : null);

    if (!title) return "-";

    const { version, showVersion } = planVersionInfo(startupPlans, row?.plan_details?.plan_type);
    const isCurrent = version > 1;

    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {title}
            {showVersion ? (
                <Box
                    component="span"
                    sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        lineHeight: 1,
                        padding: '3px 6px',
                        borderRadius: '4px',
                        backgroundColor: isCurrent ? '#EEF2FF' : '#F3F4F6',
                        color: isCurrent ? '#4338CA' : '#6B7280',
                        border: `1px solid ${isCurrent ? '#C7D2FE' : '#E5E7EB'}`,
                    }}
                >
                    {`V${version}`}
                </Box>
            ) : null}
        </Box>
    );
};

const Subscriptions = ({ tabValue = "subscriptions", setLoading = () => { }, setCount = () => { } }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const classes = useStyles();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  const paginationCookie = "backrr_subscriptions_pagination";
  const searchCookie = "backrr_subscriptions_search";
  const filtersCookie = "backrr_subscriptions_filters";

  const [search, setSearch] = React.useState(Cookies.get(searchCookie) || '')
  const [searchStatus, setSearchStatus] = React.useState(!!Cookies.get(searchCookie))
  const [reset, setReset] = React.useState(false)
  const [page, setPage] = React.useState(Number(Cookies.get(paginationCookie)) || 1);
  const [limit, setLimit] = React.useState(10);
  const [queryParams, setQueryParams] = React.useState(() => {
    const savedFilters = Cookies.get(filtersCookie);
    return savedFilters ? JSON.parse(savedFilters) : {};
  });


  const subscriptionsColumns = React.useMemo(() => ([
    { Header: 'Startup Name', accessor: 'name', id: 'name', minWidth: 260 },
    { Header: 'Purchase Date', accessor: 'purchase_date', id: 'purchase_date', minWidth: 140, align: "center" },
    { Header: 'Plan Type', accessor: 'plan_type', id: 'plan_type', minWidth: 110, align: "center" },
    { Header: 'Plan Status', accessor: 'plan_status', id: 'status', minWidth: 120, align: "center" },
    { Header: 'Next Billing Date', accessor: 'next_billing', id: 'next_billing', minWidth: 150, align: "center" },
    { Header: 'Plan Tenure', accessor: 'billing_period', id: 'billing_period', minWidth: 120, align: "center" },
    { Header: 'Amount Paid', accessor: 'amount_paid', id: 'amount_paid', minWidth: 120, align: "center" },
    { Header: 'View Invoice', accessor: 'view_invoice', id: 'actions', minWidth: 120, align: 'center' },
    { Header: 'Logs', accessor: () => 'logs', id: 'logs', minWidth: 90, align: 'center', Cell: ({ row }) => <SubscriptionLogsInfo row={row?.original} /> }
  ]), [])

  const { mutateAsync: fetchInvoicesAsync, } = FetchInvoices()

  const SubscriptionsRowActions = ({ row }) => {
    const openPayments = () => {
      // console.log(row?.original?.proration_invoices?., "original")
      // if (row?.original?.subscription_id && !row?.original?.subscription_id?.includes("admin-")) {

      //     // fetchInvoicesAsync(
      //     //     {
      //     //         subscription_id: row?.original?.subscription_id
      //     //     },
      //     //     {
      //     //         onSuccess: (response) => {
      //     //             console.log(response, "response")
      //     //         },
      //     //         onError: (error) => {
      //     //             console.log(error, "error")
      //     //         }
      //     //     })

      // }
      // else {
      //     // Placeholder: open invoices modal or navigate to payment log
      //     // Replace with proper modal/navigation later
      //     window.alert(`Open payment log for ${row?.original?.name}`)
      //     return;
      // }

      window.open(row?.original?.invoice_url)
    }
    return row?.original?.invoice_url && row?.original?.invoice_url?.length > 0 ? <IconButton onClick={openPayments} size={isSmall ? 'small' : 'medium'}>
      <VisibilityIcon />
    </IconButton> : "-"
    // return (
    //     <Secondary onClick={openPayments} startIcon={<ReceiptIcon />} size={isSmall ? 'small' : 'medium'} variant='outlined'>Payments</Secondary>
    // )
  }

  // const OneTimeRowActions = ({ row }) => {
  //     const openReport = () => {
  //         const url = row?.original?.report_url
  //         if (url) window.open(url, '_blank')
  //     }
  //     return (
  //         <IconButton onClick={openReport} size={isSmall ? 'small' : 'medium'}>
  //             <VisibilityIcon />
  //         </IconButton>
  //     )
  // }

  const CustomLink = ({ children, row }) => {
    return (
      <Box onClick={() => {
        Cookies.set("startup_id", row?.original?.startup_id);
        navigate(`/backrr_startups/view-profile`);
      }} sx={{ cursor: 'pointer' }}>
        {children}
      </Box>
    )
  }

  const { data: subscriptions, isLoading: isLoadingSubscriptions, isRefetching: isRefetchingSubscriptions, refetch: refetchSubscriptions } = FetchAllSubscriptions({ page, limit, search: searchStatus ? search : "", queryParams });

  const refetchListing = useCallback(() => {
    refetchSubscriptions();
  }, [refetchSubscriptions])

  React.useEffect(() => {
    refetchListing();
  }, [reset]);

  React.useEffect(() => {
    setCount(subscriptions?.meta?.total)
  }, [subscriptions?.meta?.total])

  const { data: startupPlans } = FetchAllStartupPlans();

  const subscriptionsData = subscriptions?.data?.map((row) => {
    // const 
    return ({
      name: row?.startup_name,
      image: row?.startup_logo,
      email: row?.startup_email,
      purchase_date: moment(row?.createdAt)?.format("DD-MMM-YYYY HH:mm:ss"),
      plan_type: <PlanTypeCell row={row} startupPlans={startupPlans} />,
      billing_period: row?.plan_details?.billing_period ? capitalizedSentence(String(row?.plan_details?.billing_period)) : row?.plan_details?.name ? "Yearly" : "-",
      next_billing: row?.charge_at && (typeof row?.charge_at !== "string") ? moment.unix(row?.charge_at)?.format("DD-MMM-YYYY") : row?.subscription_charged?.length > 0 ? moment.unix(row?.subscription_charged?.at(-1)?.charge_at)?.format("DD-MMM-YYYY") : "-",
      amount_paid: row?.subscription_id ? String(row?.subscription_id)?.includes("admin-") ? <strong>Free/Giveaway</strong> : row?.plan_details?.price && row?.plan_details?.symbol ? row?.plan_details?.symbol + (row?.plan_details?.price) : "-" : "-",
      ...row
    })
  }) || [];

  const [filterToggle, setFilterToggle] = useState(false);

  // Cross inside the search field: clears the search term only, filters are
  // reset from the filter popup.
  const handleSearchReset = () => {
    setSearch('');
    Cookies.remove(searchCookie);
    setSearchStatus(false);
    setPage(1);
    Cookies.set(paginationCookie, 1);
    setReset(!reset);
  }

  const handleFilterOpen = () => {
    setFilterToggle(true);
  }

  const handleFilterClose = () => {
    setFilterToggle(false);
  }

  const handleFilterSubmit = ({ query }) => {
    setQueryParams(query);
    Cookies.set(filtersCookie, JSON.stringify(query));
    setReset(!reset);
    setPage(1);
    setFilterToggle(false);
  }

  const handleFilterReset = () => {
    setQueryParams({});
    Cookies.remove(filtersCookie);
    setReset(!reset);
    setPage(1);
    setFilterToggle(false);
  }

  return (
    <>
      <ModalComponent open={filterToggle} onClose={handleFilterClose} style={{ maxWidth: 700, width: "100%" }}>
        <FilterPopup
          tabValue={tabValue}
          queryParams={queryParams || {}}
          setQueryParams={setQueryParams}
          handleClose={handleFilterClose}
          handleSubmit={handleFilterSubmit}
          handleReset={handleFilterReset}
        />
      </ModalComponent>


      {/* <Box sx={{ padding: '1rem 2rem' }}> */}
      <Grid container padding={"0.5rem"} marginTop={2} sx={{
        border: `1px solid ${theme.palette.customColors.b50} !important`,
        borderRadius: '8px 8px 0px 0px',
        background: theme?.palette.customColors.whiteBG, gap: "0.5rem"
      }}>
        <Grid item xs={12} sm={6} md={6} lg={6} xl={4} style={{ display: 'flex', height: 'min-content', alignItems: 'center', gap: "0.5rem" }}>

          <CustomInputField
            autoComplete="off"
            sx={{
              marginBottom: 0,
              "& > .MuiFilledInput-root": {
                borderColor: `${theme.palette.customColors.b50} !important`,
                borderRadius: '8px'
              },
              ":disabled": {
                bgcolor: theme.palette.customColors.b300,
                color: theme.palette.customColors.whiteBG
              },
            }}
            // disabled={value === "subscribed"}
            fullWidth
            value={search}
            name={`search_${Math.random().toString(36).substring(7)}`}  // Set a random name attribute
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                Cookies.set(searchCookie, search);
                refetchListing();
                if (search) {
                  setSearchStatus(true)
                }
                setPage(1);
                Cookies.set(paginationCookie, 1)
              }
            }}
            onChange={(event) => {
              setSearch(event.target.value);
              setSearchStatus(false)
            }}
            size="small"
            startAdornment={<>
              <Search sx={{ padding: "0rem 0rem 0rem 0.5rem" }} />
            </>}
            onClear={handleSearchReset}
            placeholder={'Search by Startup Name or email'}
          />
          {<Tooltip title="Filter" >
            <IconButton sx={{ marginLeft: "auto", display: { xs: 'flex', md: "flex" }, marginBlock: 'auto' }} onClick={() => handleFilterOpen()}>
              {Object.keys(queryParams)?.length > 0 ? <FilterAltRounded sx={{ color: theme?.palette?.customColors.indigo700 }} /> : <FilterAltOutlined />}
            </IconButton>
          </Tooltip>}
        </Grid>
      </Grid>

      {(isLoadingSubscriptions || isRefetchingSubscriptions) ? <>

        <Grid item xs={12} height={"100dvh"} sx={{ width: 'calc(100% - 2px)', bgColor: theme.palette.customColors.yellow_bg }} paddingInline={"0rem !important"}>
          <Box className='skeleton' minHeight={"100dvh"} sx={{ borderRadius: "0px 0px 8px 8px", border: `1px solid ${theme.palette.customColors.b50}` }} marginTop={0}>

          </Box>
        </Grid>

      </> : (
        <AsyncTableListing
          columns={subscriptionsColumns}
          data={subscriptionsData}
          // title={'Subscriptions'}
          CustomLink={CustomLink}
          RowActions={({ cell, row }) => <SubscriptionsRowActions row={row} />}
          count={subscriptions.meta?.total}
          sx={{
            borderRadius: "0px 0px 8px 8px",
            maxHeight: "calc(100dvh - 16rem)"
          }}
          maxHeight={"calc(100dvh - 16.5rem)"}
          page={page}
          setPage={setPage}
          pageCookieName={paginationCookie}
          limit={limit}
          setLimit={setLimit}
          refetchListing={refetchListing}
          reset={reset}
          noDataPlaceHolderImage={placeHolderImage}
          noDataTitle='No Results Found'
          noDataSubTitle='Try adjusting your filters to see relevant data.'
        />
      )}
      {/* </Box> */}
    </>
  )
}

export default Subscriptions